import { useState, useEffect } from 'react';
import { getInspectionFilterDates } from '@/utils/inspection.utils';
import { LoadingStatus } from '@/enums/LoadingStatus.enum';
import { setInspectionList } from '@/store/slices/inspectionsSlice';
import { store } from '@/store/store';
import { AppConfig } from '@/types/AppConfig.type';
import { ALL_CONTROL_POINTS_ID, ControlPoint } from '@/types/ControlPoint';

interface UseInspectionFiltersProps {
  config: AppConfig; // Replace with proper config type
  controlPoints: ControlPoint[]; // Replace with proper ControlPoint type
}

export const useInspectionFilters = ({ config, controlPoints }: UseInspectionFiltersProps) => {
  const datesSettings = getInspectionFilterDates(config);

  const [productCode, setProductCode] = useState<string>(() => {
    const savedProductCode = localStorage.getItem('inspectionProductCode');
    return savedProductCode || '';
  });
  const [orderReference, setOrderReference] = useState<string>(() => {
    const savedOrderReference = localStorage.getItem('inspectionOrderReference');
    return savedOrderReference || '';
  });

  const [controlPoint, setControlPoint] = useState<string>(() => {
    const savedControlPoint = localStorage.getItem('inspectionControlPoint');
    return savedControlPoint || '';
  });

  const [startDate, setStartDate] = useState<string>(() => {
    const savedStartDate = localStorage.getItem('inspectionStartDate');
    return savedStartDate || datesSettings.start;
  });

  const [endDate, setEndDate] = useState<string>(() => {
    const savedEndDate = localStorage.getItem('inspectionEndDate');
    return savedEndDate || datesSettings.end;
  });

  const [status, setStatus] = useState<{
    controlPoints: LoadingStatus;
    inspections: LoadingStatus;
    defectCodes: LoadingStatus;
  }>({
    controlPoints: LoadingStatus.IDLE,
    inspections: LoadingStatus.IDLE,
    defectCodes: LoadingStatus.IDLE,
  });

  useEffect(() => {
    if (!controlPoints?.length) {
      return;
    }
    const selectableIds = new Set(controlPoints.map((cp) => cp.id));
    const isStaleSelection =
      Boolean(controlPoint) &&
      controlPoint !== ALL_CONTROL_POINTS_ID &&
      !selectableIds.has(controlPoint);
    if (isStaleSelection) {
      const next = controlPoints.length > 1 ? ALL_CONTROL_POINTS_ID : controlPoints[0].id;
      setControlPoint(next);
      localStorage.setItem('inspectionControlPoint', next);
      store.dispatch(setInspectionList([]));
      return;
    }
    if (controlPoints.length === 1 && controlPoint === ALL_CONTROL_POINTS_ID) {
      const id = controlPoints[0].id;
      setControlPoint(id);
      localStorage.setItem('inspectionControlPoint', id);
      store.dispatch(setInspectionList([]));
      return;
    }
    if (!controlPoint) {
      setControlPoint(controlPoints[0]?.id || '');
    }
  }, [controlPoints, controlPoint]);

  const handleProductCodeChange = (productCode: string) => {
    setProductCode(productCode);
    localStorage.setItem('inspectionProductCode', productCode);
  };
  const handleOrderReferenceChange = (orderReference: string) => {
    setOrderReference(orderReference);
    localStorage.setItem('inspectionOrderReference', orderReference);
  };

  const handleControlPointChange = (nextControlPoint: string) => {
    if (nextControlPoint === controlPoint) {
      return;
    }
    setControlPoint(nextControlPoint);
    localStorage.setItem('inspectionControlPoint', nextControlPoint);
    store.dispatch(setInspectionList([]));
  };

  const handleInspectionStartDateChange = (startDate: string) => {
    setStartDate(startDate);
    localStorage.setItem('inspectionStartDate', startDate);
  };

  const handleInspectionEndDateChange = (endDate: string) => {
    setEndDate(endDate);
    localStorage.setItem('inspectionEndDate', endDate);
  };

  const handleResetFilters = () => {
    setProductCode('');
    setOrderReference('');
    localStorage.removeItem('inspectionProductCode');
    localStorage.removeItem('inspectionOrderReference');
    const defaultDates = getInspectionFilterDates(config);
    setStartDate(defaultDates.start);
    setEndDate(defaultDates.end);
    localStorage.setItem('inspectionStartDate', defaultDates.start);
    localStorage.setItem('inspectionEndDate', defaultDates.end);
    const defaultControlPoint = controlPoints[0]?.id || '';
    handleControlPointChange(defaultControlPoint);
  };

  return {
    // State
    productCode,
    orderReference,
    controlPoint,
    startDate,
    endDate,
    status,
    datesSettings,

    // Setters
    setStatus,

    // Handlers
    handleProductCodeChange,
    handleOrderReferenceChange,
    handleControlPointChange,
    handleInspectionStartDateChange,
    handleInspectionEndDateChange,
    handleResetFilters,
  };
};
